import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  login,
  register,
  logout,
  getCurrentUser,
  changePassword,
  requestPasswordReset,
  resetPassword,
} from '@/services/auth.service';
import { queryKeys } from '@/lib/queryClient';
import type { LoginRequest, RegisterRequest } from '@/types/api.types';

/**
 * Hook to get current authenticated user
 */
export const useCurrentUser = () => {
  return useQuery({
    queryKey: queryKeys.auth.user,
    queryFn: getCurrentUser,
    retry: false,
    // Only fetch if token exists
    enabled: !!localStorage.getItem('access_token'),
  });
};

/**
 * Hook for login mutation
 */
export const useLogin = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (credentials: LoginRequest) => login(credentials),
    onSuccess: (data) => {
      // Cache user data
      queryClient.setQueryData(queryKeys.auth.user, data.user);

      // Redirect to dashboard
      navigate('/dashboard');
    },
    onError: (error) => {
      console.error('Login failed:', error);
    },
  });
};

/**
 * Hook for registration mutation
 */
export const useRegister = () => {
  return useMutation({
    mutationFn: (userData: RegisterRequest) => register(userData),
    onError: (error) => {
      console.error('Registration failed:', error);
    },
  });
};

/**
 * Hook for logout mutation
 */
export const useLogout = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: logout,
    onSuccess: () => {
      // Clear all cached data
      queryClient.clear();

      // Redirect to home page
      navigate('/');
    },
    onError: (error) => {
      console.error('Logout failed:', error);
      // Still clear cache and redirect even if API call fails
      queryClient.clear();
      navigate('/');
    },
  });
};

/**
 * Hook for password change mutation
 */
export const useChangePassword = () => {
  return useMutation({
    mutationFn: ({
      currentPassword,
      newPassword,
    }: {
      currentPassword: string;
      newPassword: string;
    }) => changePassword(currentPassword, newPassword),
    onSuccess: () => {
      console.log('Password changed successfully');
    },
    onError: (error) => {
      console.error('Password change failed:', error);
    },
  });
};

/**
 * Hook for password reset request mutation
 */
export const useRequestPasswordReset = () => {
  return useMutation({
    mutationFn: (email: string) => requestPasswordReset(email),
    onSuccess: () => {
      console.log('Password reset email sent');
    },
    onError: (error) => {
      console.error('Password reset request failed:', error);
    },
  });
};

/**
 * Hook for password reset confirmation mutation
 */
export const useResetPassword = () => {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: ({ token, newPassword }: { token: string; newPassword: string }) =>
      resetPassword(token, newPassword),
    onSuccess: () => {
      console.log('Password reset successful');
      navigate('/login?reset=true');
    },
    onError: (error) => {
      console.error('Password reset failed:', error);
    },
  });
};

/**
 * Hook to check if user is authenticated
 */
export const useIsAuthenticated = (): boolean => {
  const { data: user } = useCurrentUser();
  return !!user;
};

/**
 * Hook to check if user is admin/superuser
 */
export const useIsAdmin = (): boolean => {
  const { data: user } = useCurrentUser();
  return user?.is_superuser ?? false;
};
